import type { AuthRedirect } from '../ports/auth-redirect.ts';

export interface WebAuthRedirectOptions {
  /** Origin of the app; default `globalThis.location.origin` (the page the user is on). */
  origin?: string;
  /** Path of the sign-in screen. Never a return target (it would loop); default `/dang-nhap`. */
  signInPath?: string;
  /** Where to return when there is no usable path; default `/`. */
  fallback?: string;
  /** Query parameter of the sign-in screen that carries the return path; default `next`. */
  param?: string;
}

/** Characters a browser would strip or reinterpret inside a URL (tabs, newlines, backslashes). */
// eslint-disable-next-line no-control-regex -- control characters are exactly what is refused here
const UNSAFE = /[\u0000-\u001f\u007f\\]/;

/** A `.` or `..` path segment, also percent-encoded (`%2e`): it could climb to another path. */
const DOT_SEGMENT = /(?:^|\/)(?:\.|%2e){1,2}(?:\/|$)/i;

function currentOrigin(): string {
  const location = (globalThis as { location?: { origin?: string } }).location;
  const origin = location?.origin;
  if (!origin || origin === 'null') throw new Error('createWebAuthRedirect: no page origin');
  return origin;
}

/**
 * `AuthRedirect` for the browser: return paths are same-origin paths only (one leading `/`, no
 * backslash, control character or dot segment, so the browser cannot read them as another host or
 * another path), and the social sign-in callbacks are absolute URLs on the page origin, which the API
 * trusts because it is the origin the request came from (production app, a preview alias, or
 * localhost in dev).
 */
export function createWebAuthRedirect(options: WebAuthRedirectOptions = {}): AuthRedirect {
  const origin = options.origin ?? currentOrigin();
  const signInPath = options.signInPath ?? '/dang-nhap';
  const fallback = options.fallback ?? '/';
  const param = options.param ?? 'next';

  const returnPath = (raw: string | null | undefined): string => {
    if (typeof raw !== 'string' || raw === '' || UNSAFE.test(raw)) return fallback;
    // `//evil.example` and `/\evil.example` are protocol-relative URLs, not paths.
    if (!raw.startsWith('/') || raw.startsWith('//')) return fallback;
    // From here `raw` is an absolute path on the page origin: nothing in it can change the host.
    const end = raw.search(/[?#]/);
    const pathname = end === -1 ? raw : raw.slice(0, end);
    if (DOT_SEGMENT.test(pathname)) return fallback;
    if (pathname === signInPath || pathname.startsWith(`${signInPath}/`)) return fallback;
    // The API is not a page: a callback there would show JSON (or start another auth flow).
    if (pathname === '/api' || pathname.startsWith('/api/')) return fallback;
    return raw;
  };

  return {
    returnPath,
    callbackURL: (path) => `${origin}${returnPath(path)}`,
    errorCallbackURL: (path) => {
      const target = returnPath(path);
      const query = target === fallback ? '' : `?${param}=${encodeURIComponent(target)}`;
      return `${origin}${signInPath}${query}`;
    },
  };
}
